import { useParams, Link as RouterLink } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  Box, Card, CardContent, Typography, Stack, Button, IconButton, Chip, Divider,
  Avatar, List, ListItem, ListItemAvatar, ListItemText, Skeleton, Alert,
  Snackbar, CircularProgress,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import PhoneAndroidRoundedIcon from "@mui/icons-material/PhoneAndroidRounded";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";
import { useAccess } from "../context/AccessContext";

interface Recipient {
  phone_masked: string;
  template_name?: string;
  template_language?: string;
  template_message_id?: string;
  sent_at?: string;
  status?: string;
  has_provider_response?: boolean;
}

interface ReportDetailResponse {
  tenant: string;
  report_id: string;
  template?: string;
  sent_at?: string;
  recipients: Recipient[];
  stale: boolean;
  error?: string;
}

function friendlyDate(v?: string): string {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("pt-BR"); } catch { return v; }
}

interface DownloadResponse {
  url: string;
  file_name?: string;
  expires_in_seconds?: number;
  size_bytes?: number;
}

interface ResendResponse {
  total: number;
  sent: string[];
  failed: Array<{ phone: string; reason: string }>;
  template_name?: string;
  document_filename?: string;
}

type Toast = { severity: "success" | "error" | "info" | "warning"; msg: string } | null;

export function ReportDetail() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useTenant();
  const api = useApi();
  const { can } = useAccess();
  const [toast, setToast] = useState<Toast>(null);

  const q = useQuery({
    queryKey: ["report", tenant, id],
    queryFn: () => api.call<ReportDetailResponse>(`/api/reports/${encodeURIComponent(id ?? "")}`),
    enabled: !!tenant && !!id,
    staleTime: 30_000,
  });

  const recipients = q.data?.recipients ?? [];
  const okCount = recipients.filter((r) => (r.status || "").toLowerCase().match(/sent|delivered|accepted/)).length;
  const total = recipients.length;

  const download = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("missing_id");
      return api.call<DownloadResponse>(`/api/reports/${encodeURIComponent(id)}/download`);
    },
    onSuccess: (data) => {
      if (data.url) {
        // Abre em nova aba pra evitar interromper o SPA + o Content-Disposition
        // que o signed URL carrega força o download no browser.
        window.open(data.url, "_blank", "noopener,noreferrer");
        setToast({ severity: "success", msg: `Baixando ${data.file_name ?? "relatório"}...` });
      } else {
        setToast({ severity: "warning", msg: "URL não retornada pelo servidor" });
      }
    },
    onError: (e: any) => {
      const label = e?.error === "not_found" ? "Arquivo não encontrado no bucket"
        : e?.error === "sign_failed" ? "Falha ao assinar URL (credencial GCS?)"
        : e?.error === "gcs_error" ? "GCS indisponível — tente de novo"
        : `Erro ${e?.status ?? "?"}: ${e?.message ?? e?.error ?? "desconhecido"}`;
      setToast({ severity: "error", msg: label });
    },
  });

  const resend = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("missing_id");
      return api.call<ResendResponse>(`/api/reports/${encodeURIComponent(id)}/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    },
    onSuccess: (data) => {
      const sentN = data.sent?.length ?? 0;
      const failedN = data.failed?.length ?? 0;
      if (failedN === 0) {
        setToast({ severity: "success", msg: `Reenviado para ${sentN} destinatário${sentN === 1 ? "" : "s"}` });
      } else if (sentN === 0) {
        setToast({ severity: "error", msg: `Nenhum envio funcionou (${failedN} falhas)` });
      } else {
        setToast({ severity: "warning", msg: `${sentN} enviados, ${failedN} falharam` });
      }
    },
    onError: (e: any) => {
      const label = e?.error === "no_recipients_in_history" ? "Sem destinatários no histórico"
        : e?.error === "report_not_found" ? "Relatório não encontrado"
        : e?.error === "no_whatsapp_token" ? "Token WhatsApp ausente"
        : e?.error?.startsWith("signed_url_failed") ? "Falha ao gerar URL do XLSX"
        : `Erro ${e?.status ?? "?"}: ${e?.message ?? e?.error ?? "desconhecido"}`;
      setToast({ severity: "error", msg: label });
    },
  });

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton component={RouterLink} to="/relatorios" size="small">
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">Relatórios</Typography>
      </Stack>

      <PageHeader
        title={q.data?.template ?? "Detalhe do relatório"}
        subtitle={<span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={download.isPending ? <CircularProgress size={16} /> : <FileDownloadRoundedIcon />}
              onClick={() => download.mutate()}
              disabled={download.isPending || !id}
            >
              Excel
            </Button>
            {can("relatorios.reenviar") && (
            <Button
              variant="contained"
              size="small"
              startIcon={resend.isPending ? <CircularProgress size={16} color="inherit" /> : <ReplayRoundedIcon />}
              onClick={() => resend.mutate()}
              disabled={resend.isPending || !id || total === 0}
              data-testid="reenviar"
            >
              Reenviar
            </Button>
            )}
          </Stack>
        }
      />

      {q.data?.stale && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Dados em cache ou fora do ar ({q.data.error ?? "unknown"}).
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 360px" }, gap: 2 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2, color: "primary.dark", fontWeight: 600 }}>Destinatários ({total})</Typography>

            {q.isLoading && Array.from({ length: 4 }).map((_, i) => (
              <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}><Skeleton width="60%" /><Skeleton width="40%" /></Box>
                <Skeleton width={60} />
              </Stack>
            ))}

            {!q.isLoading && recipients.length === 0 && (
              <Box sx={{
                minHeight: 200, border: "1px dashed", borderColor: "divider",
                borderRadius: 3, display: "grid", placeItems: "center",
                color: "text.secondary", textAlign: "center", p: 4, bgcolor: "background.default",
              }}>
                <Box>
                  <DescriptionRoundedIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                  <Typography variant="body2">Sem destinatários pra este envio.</Typography>
                </Box>
              </Box>
            )}

            {!q.isLoading && recipients.length > 0 && (
              <List dense disablePadding>
                {recipients.map((r, i) => {
                  const isOk = /sent|delivered|accepted/i.test(r.status || "");
                  const isFail = /fail|error|rejected/i.test(r.status || "");
                  const color = isFail ? "error" : isOk ? "success" : "info";
                  const StatusIcon = isFail ? ErrorRoundedIcon : CheckCircleRoundedIcon;
                  return (
                    <Box key={i}>
                      <ListItem disableGutters sx={{ py: 1 }}>
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main`, width: 32, height: 32 }}>
                            <PhoneAndroidRoundedIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontFamily: "monospace" }}>{r.phone_masked}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">{friendlyDate(r.sent_at)}</Typography>}
                        />
                        <Chip
                          icon={<StatusIcon sx={{ fontSize: "16px !important" }} />}
                          label={r.status || "?"}
                          size="small"
                          color={color as any}
                          variant="outlined"
                        />
                      </ListItem>
                      {i < recipients.length - 1 && <Divider component="li" />}
                    </Box>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: "primary.dark", mb: 1.5 }}>Resumo</Typography>
              <Stack spacing={0.75}>
                <RowKV label="Enviado" value={friendlyDate(q.data?.sent_at)} />
                <RowKV label="Destinatários" value={`${okCount} / ${total}`} />
                <RowKV label="Canal" value={<Chip label="WhatsApp" size="small" color="success" />} />
                <RowKV label="Template" value={<Box sx={{ fontFamily: "monospace", fontSize: 12 }}>{q.data?.template ?? "—"}</Box>} />
              </Stack>
            </CardContent>
          </Card>

          <Button component={RouterLink} to="/relatorios/agendar" variant="text"
            startIcon={<ScheduleRoundedIcon />} sx={{ alignSelf: "flex-start" }}>
            Configurar agendamento
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast?.severity ?? "info"}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ minWidth: 320 }}
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

function RowKV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" sx={{ color: "text.secondary" }}>{label}</Typography>
      <Box sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Box>
    </Stack>
  );
}
